import React, { useState, useEffect } from "react";
import { doc, collection, onSnapshot } from "firebase/firestore";
import Context from "../../Context";
import { useToast } from "../../components/ui";
import { roundDateToDate } from "../../util";
import { Header } from "../../components/modules";
import {
  FiCheckSquare,
  FiLock,
  FiShoppingBag,
  FiGift,
  FiBell,
  FiFileText,
  FiDollarSign,
  FiTrash2,
  FiRefreshCw,
  FiCheckCircle,
  FiAlertCircle,
  FiDownload,
  FiSend,
  FiChevronRight,
  FiChevronLeft,
  FiAward,
} from "react-icons/fi";

// ============================================
// Initial Prizes Schema
// ============================================
const INITIAL_PRIZES = {
  first: {
    numbers: [],
    count: 1,
    length: 6,
    digitType: "number",
    text: "รางวัลที่ 1",
    prizeAmount: 6000000,
  },
  nearBy: {
    numbers: [],
    count: 2,
    length: 6,
    digitType: "number",
    text: "รางวัลข้างเคียงรางวัลที่ 1",
    prizeAmount: 100000,
  },
  second: {
    numbers: [],
    count: 5,
    length: 6,
    digitType: "number",
    text: "รางวัลที่ 2",
    prizeAmount: 200000,
  },
  firstThree: {
    numbers: [],
    count: 2,
    length: 3,
    digitType: "firstThree",
    text: "เลขหน้า 3 ตัว",
    prizeAmount: 4000,
  },
  lastThree: {
    numbers: [],
    count: 2,
    length: 3,
    digitType: "lastThree",
    text: "เลขท้าย 3 ตัว",
    prizeAmount: 4000,
  },
  lastTwo: {
    numbers: [],
    count: 1,
    length: 2,
    digitType: "lastTwo",
    text: "เลขท้าย 2 ตัว",
    prizeAmount: 2000,
  },
  third: {
    numbers: [],
    count: 10,
    length: 6,
    digitType: "number",
    text: "รางวัลที่ 3",
    prizeAmount: 80000,
  },
  forth: {
    numbers: [],
    count: 50,
    length: 6,
    digitType: "number",
    text: "รางวัลที่ 4",
    prizeAmount: 40000,
  },
  fith: {
    numbers: [],
    count: 100,
    length: 6,
    digitType: "number",
    text: "รางวัลที่ 5",
    prizeAmount: 20000,
  },
};

const parseNumbers = (text, type) => {
  const getRegexForType = (t) => {
    if (["lastThree", "firstThree"].includes(t)) return /.{1,3}/g;
    if (t === "lastTwo") return /.{1,2}/g;
    return /.{1,6}/g;
  };
  return (text || "").replaceAll(" ", "").match(getRegexForType(type)) || [];
};

export const AllInOneDraw = () => {
  const { callApi, db, configs, updateConfig, profileState, navigate } = Context();
  const { toast } = useToast();

  const [activeStep, setActiveStep] = useState(1);
  const [loading, setLoading] = useState(false);

  // Step 1 states
  const [newRoundInput, setNewRoundInput] = useState("");
  const [selectedRoundSwitch, setSelectedRoundSwitch] = useState("");
  const [closeStoreTimeInput, setCloseStoreTimeInput] = useState("");
  const [closeCreateOrderTimeInput, setCloseCreateOrderTimeInput] = useState("");

  // Step 2 states
  const [prizesA, setPrizesA] = useState(INITIAL_PRIZES);
  const [prizesB, setPrizesB] = useState(INITIAL_PRIZES);
  const [activeSourceTab, setActiveSourceTab] = useState("A"); // 'A' or 'B'
  const [winnerOrdersPercent, setWinnerOrdersPercent] = useState(-1);

  // Step 3 states
  const [winnerUserList, setWinnerUserList] = useState([]);
  const [loadingUserList, setLoadingUserList] = useState(false);

  // Role Guard Check
  const isDev = profileState?.role === "dev";

  useEffect(() => {
    if (configs?.roundDate) {
      setSelectedRoundSwitch(configs.roundDate);
      fetchRoundPrizes();
    }
  }, [configs?.roundDate]);

  useEffect(() => {
    if (!db) return;
    const statusRef = doc(collection(db, "Status"), "makingPrizeOrders");
    const unsubscribe = onSnapshot(statusRef, (snapshot) => {
      if (snapshot.exists()) {
        const data = snapshot.get("markingOrders");
        if (data) {
          const { find = 0, marked = 0 } = data;
          setWinnerOrdersPercent(find === 0 ? 0 : Math.floor((marked / find) * 100));
        }
      }
    });
    return () => unsubscribe();
  }, [db]);

  const fetchRoundPrizes = async () => {
    if (!configs?.roundDate) return;
    const result = await callApi("prizes-getRoundPrizes", {
      roundDate: configs.roundDate,
    });
    if (!result) return;
    setPrizesA((old) => {
      const updated = { ...old };
      Object.keys(updated).forEach((key) => {
        if (result[key]) updated[key] = { ...updated[key], ...result[key] };
      });
      return updated;
    });
    setPrizesB((old) => {
      const updated = { ...old };
      Object.keys(updated).forEach((key) => {
        if (result[key]) updated[key] = { ...updated[key], ...result[key] };
      });
      return updated;
    });
  };

  if (!isDev) {
    return (
      <div className="flex flex-col justify-center items-center p-8 bg-white rounded-md shadow-md max-w-lg mx-auto my-12 gap-4">
        <FiLock className="w-16 h-16 text-red-500" />
        <h2 className="text-xl font-bold text-gray-800">เข้าถึงหน้านี้ไม่ได้</h2>
        <p className="text-sm font-semibold text-gray-600 text-center">
          หน้านี้ถูกจำกัดการเข้าถึงเฉพาะบัญชีผู้ใช้ที่มีสิทธิ์บทบาท dev เท่านั้น
        </p>
      </div>
    );
  }

  // ============================================
  // Step 1 Actions
  // ============================================
  const handleAddNewRound = async () => {
    if (!newRoundInput.trim()) return alert("กรุณาระบุวันที่งวดใหม่ เช่น 2026-09-01");
    if (!window.confirm(`ยืนยันการเพิ่มงวดใหม่ ${newRoundInput}?`)) return;
    setLoading(true);
    const res = await callApi("configs-addNewRoundDate", { roundDate: newRoundInput });
    setLoading(false);
    if (res?.status) {
      toast({ className: "bg-green text-white", title: "เพิ่มงวดใหม่สำเร็จ" });
      setNewRoundInput("");
      updateConfig();
    } else {
      toast({ className: "bg-red-500 text-white", title: res?.message || "เพิ่มงวดล้มเหลว" });
    }
  };

  const handleSwitchRound = async () => {
    if (!selectedRoundSwitch) return;
    if (!window.confirm(`ยืนยันการเปลี่ยนไปทำงานในงวด ${selectedRoundSwitch}?`)) return;
    setLoading(true);
    await callApi("configs-edit", { key: "roundDate", value: selectedRoundSwitch });
    await updateConfig();
    setLoading(false);
    toast({ className: "bg-green text-white", title: `สลับไปงวด ${selectedRoundSwitch} เรียบร้อย` });
  };

  const handleSaveCloseStoreTime = async () => {
    if (!closeStoreTimeInput) return alert("กรุณาเลือกเวลาปิดใส่ตะกร้าหน้าร้าน");
    const timestamp = new Date(closeStoreTimeInput).getTime();
    if (isNaN(timestamp)) return alert("รูปแบบวันที่/เวลาไม่ถูกต้อง");
    setLoading(true);
    await callApi("configs-edit", { key: "closeRoundDateTime", value: timestamp });
    await updateConfig();
    setLoading(false);
    toast({ className: "bg-green text-white", title: "อัปเดตเวลาปิดเลือกสลากหน้าร้านสำเร็จ" });
  };

  const handleSaveCloseCreateOrderTime = async () => {
    if (!closeCreateOrderTimeInput) return alert("กรุณาเลือกเวลาปิดสร้างคำสั่งซื้อ");
    const timestamp = new Date(closeCreateOrderTimeInput).getTime();
    if (isNaN(timestamp)) return alert("รูปแบบวันที่/เวลาไม่ถูกต้อง");
    setLoading(true);
    await callApi("configs-edit", { key: "closeCreateOrderDateTime", value: timestamp });
    await updateConfig();
    setLoading(false);
    toast({ className: "bg-green text-white", title: "อัปเดตเวลาปิดสร้างคำสั่งซื้อสำเร็จ" });
  };

  const handleToggleStore = async (isOpen) => {
    const actionText = isOpen ? "เปิดให้เลือกสลากใส่ตะกร้า" : "ปิดไม่ให้เลือกสลากใส่ตะกร้าเพิ่ม";
    if (!window.confirm(`คุณต้องการ ${actionText} ใช่หรือไม่?`)) return;
    setLoading(true);
    await callApi("configs-edit", { key: "isOpen.store", value: isOpen });
    await updateConfig();
    setLoading(false);
    toast({ className: "bg-green text-white", title: `${actionText} เรียบร้อย` });
  };

  const handleToggleCreateOrder = async (isOpen) => {
    const actionText = isOpen ? "เปิดให้สร้างคำสั่งซื้อจากตะกร้า" : "ปิดไม่ให้สร้างคำสั่งซื้อจากตะกร้าแล้ว";
    if (!window.confirm(`คุณต้องการ ${actionText} ใช่หรือไม่?`)) return;
    setLoading(true);
    await callApi("configs-edit", { key: "isOpen.createOrder", value: isOpen });
    await updateConfig();
    setLoading(false);
    toast({ className: "bg-green text-white", title: `${actionText} เรียบร้อย` });
  };

  const handleClearAllCart = async () => {
    if (!window.confirm("⚠️ คำเตือน: คุณกำลังจะล้างตะกร้าสินค้าทั้งหมดของลูกค้าและคืนสลาก\nยืนยันการล้างตะกร้าใช่หรือไม่?")) return;
    setLoading(true);
    const res = await callApi("carts-clearAllCart");
    setLoading(false);
    if (res?.status) {
      toast({ className: "bg-green text-white", title: "ล้างตะกร้าสินค้าเรียบร้อยแล้ว" });
    }
  };

  const handleCancelPendingOrders = async () => {
    if (!window.confirm("⚠️ คำเตือน: ยืนยันการยกเลิกคำสั่งซื้อที่ค้างสถานะรอชำระเงิน (pending) ทั้งหมดหรือไม่?")) return;
    setLoading(true);
    const res = await callApi("orders-canceledPendingOrder");
    setLoading(false);
    if (res?.status) {
      toast({ className: "bg-green text-white", title: "ยกเลิกออเดอร์รอชำระเงินเรียบร้อยแล้ว" });
    }
  };

  // ============================================
  // Step 2 Actions
  // ============================================
  const validatePrizeSet = (prizeObj) => {
    return Object.entries(prizeObj).every(([, value]) => {
      const { numbers, count, length, text } = value;
      if (!numbers || numbers.length !== count) {
        toast({ className: "bg-red-500 text-white", title: `${text} มีจำนวนตัวเลขไม่ครบ (${numbers?.length || 0}/${count})` });
        return false;
      }
      if (numbers.some((n) => n.length !== length || isNaN(n))) {
        toast({ className: "bg-red-500 text-white", title: `${text} มีเลขบางตัวไม่ครบ ${length} หลัก หรือไม่ใช่ตัวเลข` });
        return false;
      }
      return true;
    });
  };

  const checkSourceMatch = () => {
    return Object.keys(INITIAL_PRIZES).every((key) => {
      const numA = (prizesA[key]?.numbers || []).join(",");
      const numB = (prizesB[key]?.numbers || []).join(",");
      return numA === numB;
    });
  };

  const handleSavePrizes = async () => {
    const targetPrizes = activeSourceTab === "A" ? prizesA : prizesB;
    if (!validatePrizeSet(targetPrizes)) return;

    if (!checkSourceMatch()) {
      if (!window.confirm("⚠️ คำเตือน: เลขรางวัลจาก Source A และ Source B ไม่ตรงกัน!\nคุณยังคงต้องการบันทึกรางวัลหรือไม่?")) {
        return;
      }
    }

    setLoading(true);
    const res = Object.entries(targetPrizes).reduce((acc, [key, value]) => {
      acc[key] = {
        numbers: value.numbers,
        prizeAmount: value.prizeAmount,
        digitType: value.digitType,
      };
      return acc;
    }, {});

    const status = await callApi(
      "prizes-editAll",
      { id: configs.roundDate, value: res },
      { notShowLoading: true }
    );
    setLoading(false);
    if (status) {
      toast({ className: "bg-green text-white", title: "บันทึกรางวัลเข้าสู่ระบบสำเร็จ" });
    } else {
      toast({ className: "bg-red-500 text-white", title: "บันทึกรางวัลไม่สำเร็จ" });
    }
  };

  const handleCheckAllOrders = async () => {
    if (!window.confirm(`ยืนยันการตรวจรางวัลคำสั่งซื้อทั้งหมดในงวด ${configs.roundDate}?`)) return;
    setLoading(true);
    await callApi("prizes-getAllWinnerOrders");
    setLoading(false);
    toast({ className: "bg-green text-white", title: "เริ่มกระบวนการตรวจรางวัลในออเดอร์แล้ว" });
  };

  const handleFindBookLotteries = async () => {
    if (!window.confirm(`ยืนยันการหารางวัลสลากในเล่มงวด ${configs.roundDate}?`)) return;
    setLoading(true);
    await callApi("prizes-getAllWinnerLotteries");
    setLoading(false);
    toast({ className: "bg-green text-white", title: "เริ่มกระบวนการหารางวัลในเล่มแล้ว" });
  };

  // ============================================
  // Step 3 Actions
  // ============================================
  const handleToggleAwardReady = async (isReady) => {
    const label = isReady ? "เปิดประกาศรางวัลให้ลูกค้าเช็คได้" : "ปิดการประกาศรางวัล";
    if (!window.confirm(`ยืนยันการ ${label}?`)) return;
    setLoading(true);
    await callApi("configs-edit", { key: "isAwardReady", value: isReady });
    await updateConfig();
    setLoading(false);
    toast({ className: "bg-green text-white", title: `${label} เรียบร้อยแล้ว` });
  };

  const handleSendLineAlert = async () => {
    if (!window.confirm("ยืนยันการส่งข้อความแจ้งเตือนรางวัลทาง LINE ไปยังผู้ถูกรางวัลทั้งหมด?")) return;
    setLoading(true);
    const res = await callApi("prizes-alertPrizeToUser");
    setLoading(false);
    if (res) {
      toast({ className: "bg-green text-white", title: "ส่งแจ้งเตือนทาง LINE เรียบร้อยแล้ว" });
    }
  };

  const handleFetchWinnersForPhone = async () => {
    setLoadingUserList(true);
    const res = await callApi("reports-reportUserPrizes", { roundDate: configs.roundDate });
    setLoadingUserList(false);
    if (Array.isArray(res)) {
      setWinnerUserList(res);
      toast({ className: "bg-green text-white", title: `ดึงข้อมูลผู้ถูกรางวัลสำเร็จ (${res.length} รายการ)` });
    }
  };

  const handleExportPhoneCSV = () => {
    if (!winnerUserList.length) return alert("ไม่มีข้อมูลผู้ถูกรางวัลให้ดาวน์โหลด");
    const headers = ["ID", "ชื่อ", "นามสกุล", "เบอร์โทร", "ประเภทแจ้งเตือน", "ยอดรางวัล (บาท)", "ธนาคาร", "เลขบัญชี", "ชื่อบัญชี"];
    const rows = winnerUserList.map((item) => [
      item.id || "",
      item.firstName || "",
      item.lastName || "",
      item.phone || "",
      item.notifyType || "",
      item.totalPrizes || 0,
      item.bankName || "",
      `'${item.accNo || ""}`,
      item.accName || "",
    ]);
    const csvContent =
      "data:text/csv;charset=utf-8,\uFEFF" +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `winners_phone_alert_${configs.roundDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ============================================
  // Step 5 Actions
  // ============================================
  const handleClearAllBlocks = async () => {
    if (
      !window.confirm(
        "⚠️ คำเตือนสำคัญ: คุณกำลังจะล้างสลากคงเหลือและลบเอกสารทั้งหมดใน collection 'Blocks'\nสลากที่เหลือในบล็อกจะถูกดึงออก เพื่อไม่ให้ติดค้างไปรวมกับงวดหน้า\n\nยืนยันลบทั้งหมดใช่หรือไม่?"
      )
    )
      return;
    setLoading(true);
    const res = await callApi("blocks-pullFromBlock");
    setLoading(false);
    if (res?.status) {
      toast({
        className: "bg-green text-white",
        title: `ล้างและลบบล็อกสลากสำเร็จ (${res.count || 0} ใบ)`,
      });
    } else {
      toast({ className: "bg-red-500 text-white", title: res?.message || "ล้างบล็อกไม่สำเร็จ" });
    }
  };

  const handleAutoDepositAffiliate = async () => {
    if (!window.confirm(`ยืนยันการคำนวณรายได้ Affiliate ประจำงวด ${configs.roundDate}?`)) return;
    setLoading(true);
    const res = await callApi("affiliates-autoDepositRoundDateAffiliate");
    setLoading(false);
    if (res) {
      toast({ className: "bg-green text-white", title: "คำนวณรายได้ Affiliate เรียบร้อยแล้ว" });
    }
  };

  const handleAddRoundedPoint = async () => {
    if (!window.confirm(`ยืนยันการคำนวณคะแนนผู้ซื้อในงวด ${configs.roundDate}?`)) return;
    setLoading(true);
    const res = await callApi("points-addRoundedPoint");
    setLoading(false);
    if (res) {
      toast({ className: "bg-green text-white", title: "คำนวณคะแนนผู้ซื้อเรียบร้อยแล้ว" });
    }
  };

  const handleResetExpiredPoints = async () => {
    if (!window.confirm("⚠️ คำเตือน: ยืนยันการรีเซ็ตคะแนนสะสมของสมาชิกที่หมดอายุในเดือนนี้ (สิ้นเดือน) หรือไม่?")) return;
    setLoading(true);
    const res = await callApi("points-resetExpiredPoint");
    setLoading(false);
    if (res) {
      toast({ className: "bg-green text-white", title: "รีเซ็ตคะแนนหมดอายุสำเร็จ" });
    }
  };

  const stepTitles = [
    "1. เตรียมปิดงวด",
    "2. สร้าง & ตรวจรางวัล",
    "3. ประกาศผล & แจ้งเตือน",
    "4. รายงานประจำงวด",
    "5. สรุปรายได้, คะแนน & ล้างบล็อก",
  ];

  return (
    <div className="w-full max-w-[1400px] mx-auto pb-16 flex flex-col gap-6" id="all-in-one-main">
      <Header
        header="Dev All-in-One Draw Control"
        subHeader="ระบบควบคุมและดำเนินการปิดงวดแบบรวมทุกขั้นตอน (สำหรับ Dev)"
        icon={<FiCheckSquare className="text-blue" />}
      />

      {/* Stepper Navigation Header */}
      <div className="bg-white rounded-md shadow-md p-4 flex flex-col md:flex-row justify-between items-center gap-2 border-b border-gray-200">
        {stepTitles.map((title, index) => {
          const stepNum = index + 1;
          const isActive = activeStep === stepNum;
          return (
            <button
              key={stepNum}
              onClick={() => setActiveStep(stepNum)}
              className={`flex-1 w-full py-3 px-4 rounded-md text-sm font-bold duration-150 transition-all flex items-center justify-center gap-2 ${isActive
                ? "bg-blue text-white shadow-md scale-102"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
            >
              <span>{title}</span>
            </button>
          );
        })}
      </div>

      {/* STEP 1: PREPARE & CLOSE ROUND */}
      {activeStep === 1 && (
        <div className="flex flex-col gap-6">
          {/* Status Monitor Section (4 Cards) */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex flex-col justify-center p-5 items-center shadow-md bg-white rounded-md w-full gap-1 border-l-4 border-blue">
              <span className="text-xs font-semibold text-gray-500">งวดปัจจุบันที่ทำงาน</span>
              <span className="text-xl font-bold text-gray-800">
                {configs?.roundDate ? roundDateToDate(configs.roundDate) : "ยังไม่ได้เลือกงวด"}
              </span>
            </div>

            <div className="flex flex-col justify-center p-5 items-center shadow-md bg-white rounded-md w-full gap-1 border-l-4 border-emerald-500">
              <span className="text-xs font-semibold text-gray-500">1. การเลือกสลากใส่ตะกร้า</span>
              <span className={`text-lg font-bold ${configs?.isOpen?.store ? "text-emerald-600" : "text-red-500"}`}>
                {configs?.isOpen?.store ? "🟢 เปิดเลือกใส่ตะกร้า" : "🔴 ปิดเลือกใส่ตะกร้า"}
              </span>
              <span className="text-xs font-semibold text-gray-400">
                ปิด: {configs?.closeRoundDateTime ? new Date(configs.closeRoundDateTime).toLocaleTimeString("th-TH") : "ไม่ตั้ง"}
              </span>
            </div>

            <div className="flex flex-col justify-center p-5 items-center shadow-md bg-white rounded-md w-full gap-1 border-l-4 border-teal-500">
              <span className="text-xs font-semibold text-gray-500">2. การสร้างคำสั่งซื้อจากตะกร้า</span>
              <span className={`text-lg font-bold ${configs?.isOpen?.createOrder ? "text-teal-600" : "text-red-500"}`}>
                {configs?.isOpen?.createOrder ? "🟢 เปิดสร้างออเดอร์" : "🔴 ปิดสร้างออเดอร์"}
              </span>
              <span className="text-xs font-semibold text-gray-400">
                ปิด: {configs?.closeCreateOrderDateTime ? new Date(configs.closeCreateOrderDateTime).toLocaleTimeString("th-TH") : "ไม่ตั้ง"}
              </span>
            </div>

            <div className="flex flex-col justify-center p-5 items-center shadow-md bg-white rounded-md w-full gap-1 border-l-4 border-amber-500">
              <span className="text-xs font-semibold text-gray-500">วันเปิดขายงวดใหม่</span>
              <span className="text-sm font-bold text-gray-800">
                {configs?.nextRoundDateTime
                  ? new Date(configs.nextRoundDateTime).toLocaleString("th-TH")
                  : "ไม่ได้ตั้งเวลาเปิด"}
              </span>
            </div>
          </div>

          {/* Form Controls Section */}
          <div className="bg-white rounded-md shadow-md p-6 flex flex-col gap-6">
            <h3 className="text-xl font-bold text-gray-800 border-b pb-3">การจัดการงวดและเวลาขายอัตโนมัติ</h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Add New Round */}
              <div className="flex flex-col gap-2 p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="text-xs font-semibold text-gray-700">1. เพิ่มงวดใหม่</label>
                <input
                  type="text"
                  placeholder="เช่น 2026-09-01"
                  value={newRoundInput}
                  onChange={(e) => setNewRoundInput(e.target.value)}
                  className="p-2 border rounded-md text-sm font-semibold border-gray-300"
                />
                <button
                  onClick={handleAddNewRound}
                  disabled={loading}
                  className="py-2 px-3 text-xs font-bold rounded-md bg-blue text-white hover:opacity-80 transition-all disabled:opacity-50"
                >
                  + เพิ่มงวดใหม่
                </button>
              </div>

              {/* Switch Active Round */}
              <div className="flex flex-col gap-2 p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="text-xs font-semibold text-gray-700">2. สลับงวดทำงาน</label>
                <select
                  value={selectedRoundSwitch}
                  onChange={(e) => setSelectedRoundSwitch(e.target.value)}
                  className="p-2 border rounded-md text-sm font-semibold border-gray-300 bg-white"
                >
                  {(configs?.roundDates || []).map((rd) => (
                    <option key={rd} value={rd}>
                      {rd} ({roundDateToDate(rd)})
                    </option>
                  ))}
                </select>
                <button
                  onClick={handleSwitchRound}
                  disabled={loading}
                  className="py-2 px-3 text-xs font-bold rounded-md bg-indigo-600 text-white hover:opacity-80 transition-all disabled:opacity-50"
                >
                  🔄 สลับงวดทำงาน
                </button>
              </div>

              {/* Set Auto Close Store Time */}
              <div className="flex flex-col gap-2 p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="text-xs font-semibold text-gray-700">3. เวลาปิดเลือกใส่ตะกร้า</label>
                <input
                  type="datetime-local"
                  value={closeStoreTimeInput}
                  onChange={(e) => setCloseStoreTimeInput(e.target.value)}
                  className="p-2 border rounded-md text-xs font-semibold border-gray-300"
                />
                <button
                  onClick={handleSaveCloseStoreTime}
                  disabled={loading}
                  className="py-2 px-3 text-xs font-bold rounded-md bg-amber-600 text-white hover:opacity-80 transition-all disabled:opacity-50"
                >
                  💾 ตั้งปิดใส่ตะกร้า
                </button>
              </div>

              {/* Set Auto Close CreateOrder Time */}
              <div className="flex flex-col gap-2 p-4 bg-gray-50 rounded-md border border-gray-200">
                <label className="text-xs font-semibold text-gray-700">4. เวลาปิดสร้างคำสั่งซื้อ</label>
                <input
                  type="datetime-local"
                  value={closeCreateOrderTimeInput}
                  onChange={(e) => setCloseCreateOrderTimeInput(e.target.value)}
                  className="p-2 border rounded-md text-xs font-semibold border-gray-300"
                />
                <button
                  onClick={handleSaveCloseCreateOrderTime}
                  disabled={loading}
                  className="py-2 px-3 text-xs font-bold rounded-md bg-rose-600 text-white hover:opacity-80 transition-all disabled:opacity-50"
                >
                  💾 ตั้งปิดสร้างออเดอร์
                </button>
              </div>
            </div>

            <hr className="my-2" />

            <h3 className="text-xl font-bold text-gray-800">ปุ่มสลับสิทธิ์การทำงาน และการล้างตะกร้า/ออเดอร์ (Immediate Actions)</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <button
                onClick={() => handleToggleStore(!configs?.isOpen?.store)}
                disabled={loading}
                className={`py-3 px-4 text-xs font-bold rounded-md text-white transition-all shadow-sm ${configs?.isOpen?.store
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
              >
                {configs?.isOpen?.store ? "⛔ สั่งปิดเลือกใส่ตะกร้าหน้าร้าน" : "🟢 สั่งเปิดเลือกใส่ตะกร้า"}
              </button>

              <button
                onClick={() => handleToggleCreateOrder(!configs?.isOpen?.createOrder)}
                disabled={loading}
                className={`py-3 px-4 text-xs font-bold rounded-md text-white transition-all shadow-sm ${configs?.isOpen?.createOrder
                  ? "bg-red-600 hover:bg-red-700"
                  : "bg-teal-600 hover:bg-teal-700"
                  }`}
              >
                {configs?.isOpen?.createOrder ? "⛔ สั่งปิดสร้างออเดอร์จากตะกร้า" : "🟢 สั่งเปิดสร้างออเดอร์"}
              </button>

              <button
                onClick={handleClearAllCart}
                disabled={loading}
                className="py-3 px-4 text-xs font-bold rounded-md bg-rose-500 text-white hover:bg-rose-600 transition-all shadow-sm flex items-center justify-center gap-1"
              >
                <FiTrash2 className="w-4 h-4" />
                <span>ล้างตะกร้าสินค้าทั้งหมด (Clear Carts)</span>
              </button>

              <button
                onClick={handleCancelPendingOrders}
                disabled={loading}
                className="py-3 px-4 text-xs font-bold rounded-md bg-orange-500 text-white hover:bg-orange-600 transition-all shadow-sm flex items-center justify-center gap-1"
              >
                <FiRefreshCw className="w-4 h-4" />
                <span>ยกเลิกออเดอร์รอจ่าย (Cancel Pending)</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 2: CREATE & CHECK REWARDS */}
      {activeStep === 2 && (
        <div className="flex flex-col gap-6">
          <div className="bg-white rounded-md shadow-md p-6 flex flex-col gap-6">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-b pb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  ตรวจสอบและบันทึกรางวัล (งวด {roundDateToDate(configs?.roundDate)})
                </h3>
                <p className="text-sm font-semibold text-gray-500">
                  เปรียบเทียบผลรางวัล 2 ชุดข้อมูล (Dual-Source) ก่อนกดยืนยันบันทึกเข้าระบบ
                </p>
              </div>

              {/* Source Tabs */}
              <div className="flex bg-gray-100 p-1 rounded-md">
                <button
                  onClick={() => setActiveSourceTab("A")}
                  className={`py-1.5 px-4 text-sm font-bold rounded-md transition-all ${activeSourceTab === "A" ? "bg-blue text-white shadow-sm" : "text-gray-600"
                    }`}
                >
                  Source A (ชุดที่ 1)
                </button>
                <button
                  onClick={() => setActiveSourceTab("B")}
                  className={`py-1.5 px-4 text-sm font-bold rounded-md transition-all ${activeSourceTab === "B" ? "bg-blue text-white shadow-sm" : "text-gray-600"
                    }`}
                >
                  Source B (ชุดที่ 2)
                </button>
              </div>
            </div>

            {/* Match Indicator Summary */}
            <div
              className={`p-4 rounded-md flex items-center justify-between border ${checkSourceMatch()
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-red-50 border-red-200 text-red-800"
                }`}
            >
              <div className="flex items-center gap-2 font-bold text-sm">
                {checkSourceMatch() ? (
                  <>
                    <FiCheckCircle className="w-5 h-5 text-emerald-600" />
                    <span>ข้อมูลรางวัลระหว่าง Source A และ Source B ตรงกัน 100% (พร้อมบันทึก)</span>
                  </>
                ) : (
                  <>
                    <FiAlertCircle className="w-5 h-5 text-red-600" />
                    <span>มีเลขรางวัลบางหมวดระหว่าง Source A และ Source B ไม่ตรงกัน! กรุณาตรวจสอบ</span>
                  </>
                )}
              </div>
            </div>

            {/* Prize Entry Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Object.entries(activeSourceTab === "A" ? prizesA : prizesB).map(([key, prizeVal]) => {
                const numA = (prizesA[key]?.numbers || []).join(" ");
                const numB = (prizesB[key]?.numbers || []).join(" ");
                const isMatch = numA === numB;

                return (
                  <div
                    key={key}
                    className={`p-3 rounded-md border flex flex-col gap-2 ${isMatch ? "bg-white border-gray-300" : "bg-red-50 border-red-300"
                      }`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-bold text-gray-800">{prizeVal.text}</span>
                      <span className="text-xs font-semibold text-gray-500">
                        ({prizeVal.numbers?.length || 0}/{prizeVal.count})
                      </span>
                    </div>

                    <textarea
                      rows={2}
                      className="w-full p-2 text-sm font-semibold border rounded-md border-gray-300 focus:outline-none focus:border-blue"
                      value={(prizeVal.numbers || []).join(" ")}
                      onChange={(e) => {
                        const arr = parseNumbers(e.target.value, key);
                        const setter = activeSourceTab === "A" ? setPrizesA : setPrizesB;
                        setter((old) => ({
                          ...old,
                          [key]: { ...old[key], numbers: arr },
                        }));
                      }}
                      placeholder={`เว้นวรรคเพื่อแยกเลข (${prizeVal.length} หลัก)`}
                    />

                    {!isMatch && (
                      <span className="text-xs font-bold text-red-600">
                        ⚠️ Source A: {numA || "-"} | Source B: {numB || "-"}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Prize Action Buttons */}
            <div className="flex flex-col md:flex-row gap-4 pt-4 border-t">
              <button
                onClick={handleSavePrizes}
                disabled={loading}
                className="py-2.5 px-6 text-sm font-bold rounded-md bg-blue text-white hover:opacity-80 transition-all shadow-md disabled:opacity-50"
              >
                💾 1. บันทึกผลรางวัลเข้า DB
              </button>

              <button
                onClick={handleCheckAllOrders}
                disabled={winnerOrdersPercent > 0 || loading}
                className="py-2.5 px-6 text-sm font-bold rounded-md bg-teal-600 text-white hover:opacity-80 transition-all shadow-md disabled:opacity-50"
              >
                🎯 2. ตรวจรางวัลในทุกคำสั่งซื้อ ({winnerOrdersPercent >= 0 ? `${winnerOrdersPercent}%` : "เริ่มตรวจ"})
              </button>

              <button
                onClick={handleFindBookLotteries}
                disabled={loading}
                className="py-2.5 px-6 text-sm font-bold rounded-md bg-amber-500 text-white hover:opacity-80 transition-all shadow-md disabled:opacity-50"
              >
                📚 3. หารางวัลในเล่มสลาก
              </button>
            </div>
          </div>
        </div>
      )}

      {/* STEP 3: ANNOUNCE & NOTIFY */}
      {activeStep === 3 && (
        <div className="flex flex-col gap-6">
          <div className="bg-white rounded-md shadow-md p-6 flex flex-col gap-6">
            <h3 className="text-xl font-bold text-gray-800 border-b pb-3">
              การประกาศรางวัลและการส่งการแจ้งเตือน (งวด {roundDateToDate(configs?.roundDate)})
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Toggle Customer Award Check */}
              <div className="flex flex-col justify-between p-6 bg-gray-50 rounded-md border border-gray-200 gap-4">
                <div>
                  <span className="text-lg font-bold text-gray-800">1. สวิตช์ประกาศรางวัล (isAwardReady)</span>
                  <p className="text-sm font-semibold text-gray-500">
                    ควบคุมการแสดงผลปุ่มตรวจรางวัลในหน้าเว็บไซต์ของลูกค้า
                  </p>
                </div>
                <button
                  onClick={() => handleToggleAwardReady(!configs?.isAwardReady)}
                  disabled={loading}
                  className={`py-3 px-4 text-sm font-bold rounded-md text-white transition-all shadow-sm ${configs?.isAwardReady ? "bg-emerald-600 hover:bg-emerald-700" : "bg-amber-500 hover:bg-amber-600"
                    }`}
                >
                  {configs?.isAwardReady ? "🟢 ปัจจุบัน: เปิดประกาศรางวัลแล้ว (กดเพื่อปิด)" : "🟡 ปัจจุบัน: ปิดอยู่ (กดเพื่อเปิดประกาศรางวัล)"}
                </button>
              </div>

              {/* LINE Notification Alert */}
              <div className="flex flex-col justify-between p-6 bg-gray-50 rounded-md border border-gray-200 gap-4">
                <div>
                  <span className="text-lg font-bold text-gray-800">2. แจ้งเตือนผู้ถูกรางวัลทาง LINE</span>
                  <p className="text-sm font-semibold text-gray-500">
                    ส่งข้อความอัตโนมัติหาลูกค้าทุกคนที่ถูกรางวัลผ่านช่องทาง LINE
                  </p>
                </div>
                <button
                  onClick={handleSendLineAlert}
                  disabled={loading}
                  className="py-3 px-4 text-sm font-bold rounded-md bg-emerald-500 text-white hover:bg-emerald-600 transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  <FiSend className="w-4 h-4" />
                  <span>ส่งแจ้งเตือนรางวัลทาง LINE ทั้งหมด</span>
                </button>
              </div>
            </div>

            <hr />

            {/* Phone Call Winner Data Export */}
            <div className="flex flex-col gap-4">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <div>
                  <h4 className="text-lg font-bold text-gray-800">3. ดึงข้อมูลเบอร์โทรสำหรับทีมโทรแจ้งรางวัล</h4>
                  <p className="text-sm font-semibold text-gray-500">
                    ดึงรายชื่อและเบอร์โทรศัพท์ผู้ถูกรางวัลเพื่อส่งต่อให้ทีม Call Center
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleFetchWinnersForPhone}
                    disabled={loadingUserList}
                    className="py-2.5 px-4 text-sm font-bold rounded-md bg-blue text-white hover:opacity-80 transition-all"
                  >
                    {loadingUserList ? "กำลังดึงข้อมูล..." : "🔄 ดึงข้อมูลผู้ถูกรางวัล"}
                  </button>

                  <button
                    onClick={handleExportPhoneCSV}
                    disabled={!winnerUserList.length}
                    className="py-2.5 px-4 text-sm font-bold rounded-md bg-emerald-600 text-white hover:opacity-80 transition-all disabled:opacity-50 flex items-center gap-2"
                  >
                    <FiDownload className="w-4 h-4" />
                    <span>ดาวน์โหลดไฟล์ CSV ({winnerUserList.length})</span>
                  </button>
                </div>
              </div>

              {/* Preview Table */}
              {winnerUserList.length > 0 && (
                <div className="max-h-64 overflow-y-auto border rounded-md">
                  <table className="w-full text-left border-collapse">
                    <thead>
                      <tr className="bg-gray-100 text-xs font-bold text-gray-600 border-b">
                        <th className="p-2">ชื่อ-นามสกุล</th>
                        <th className="p-2">เบอร์โทร</th>
                        <th className="p-2">ช่องทางแจ้งเตือน</th>
                        <th className="p-2">ยอดรางวัล (บาท)</th>
                        <th className="p-2">ธนาคาร</th>
                        <th className="p-2">เลขบัญชี</th>
                      </tr>
                    </thead>
                    <tbody className="text-sm font-semibold text-gray-700 divide-y">
                      {winnerUserList.slice(0, 50).map((u, i) => (
                        <tr key={i} className="hover:bg-gray-50">
                          <td className="p-2">{u.firstName} {u.lastName}</td>
                          <td className="p-2">{u.phone || "-"}</td>
                          <td className="p-2">{u.notifyType || "-"}</td>
                          <td className="p-2 font-bold text-emerald-600">{(u.totalPrizes || 0).toLocaleString()}</td>
                          <td className="p-2">{u.bankName || "-"}</td>
                          <td className="p-2">{u.accNo || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* STEP 4: QUICK REPORTS */}
      {activeStep === 4 && (
        <div className="flex flex-col gap-6">
          <div className="bg-white rounded-md shadow-md p-6 flex flex-col gap-6">
            <h3 className="text-xl font-bold text-gray-800 border-b pb-3">
              ทางลัดรายงานสรุปประจำงวด (Quick Reports)
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                onClick={() => navigate("/reports/user-prizes")}
                className="p-6 rounded-md border border-gray-200 bg-gray-50 hover:bg-blue hover:text-white cursor-pointer transition-all flex justify-between items-center group shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <FiGift className="w-8 h-8 text-blue group-hover:text-white" />
                  <div className="flex flex-col">
                    <span className="text-lg font-bold">1. รายงานรางวัลผู้</span>
                    <span className="text-sm font-semibold opacity-80">ดูรายละเอียดรางวัลของลูกค้าแยกตามงวด</span>
                  </div>
                </div>
                <FiChevronRight className="w-6 h-6" />
              </div>

              <div
                onClick={() => navigate("/reports/lotterie-books-import")}
                className="p-6 rounded-md border border-gray-200 bg-gray-50 hover:bg-blue hover:text-white cursor-pointer transition-all flex justify-between items-center group shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <FiFileText className="w-8 h-8 text-blue group-hover:text-white" />
                  <div className="flex flex-col">
                    <span className="text-lg font-bold">2. รายงานการนำเข้าเล่มสลาก</span>
                    <span className="text-sm font-semibold opacity-80">ตรวจสอบเล่มสลากทั้งหมดในระบบ</span>
                  </div>
                </div>
                <FiChevronRight className="w-6 h-6" />
              </div>

              <div
                onClick={() => navigate("/reports/winner")}
                className="p-6 rounded-md border border-gray-200 bg-gray-50 hover:bg-blue hover:text-white cursor-pointer transition-all flex justify-between items-center group shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <FiAward className="w-8 h-8 text-blue group-hover:text-white" />
                  <div className="flex flex-col">
                    <span className="text-lg font-bold">3. รายงานผู้ถูกรางวัล (Winner Summary)</span>
                    <span className="text-sm font-semibold opacity-80">สรุปยอดรวมผู้โชคดีและการรับเงินรางวัล</span>
                  </div>
                </div>
                <FiChevronRight className="w-6 h-6" />
              </div>

              <div
                onClick={() => navigate("/reports/prizes-user")}
                className="p-6 rounded-md border border-gray-200 bg-gray-50 hover:bg-blue hover:text-white cursor-pointer transition-all flex justify-between items-center group shadow-sm"
              >
                <div className="flex items-center gap-3">
                  <FiDollarSign className="w-8 h-8 text-blue group-hover:text-white" />
                  <div className="flex flex-col">
                    <span className="text-lg font-bold">4. รายงานการโอนเงินรางวัล</span>
                    <span className="text-sm font-semibold opacity-80">รายชื่อผู้ถูกรางวัลที่ต้องโอนเงินรางวัล</span>
                  </div>
                </div>
                <FiChevronRight className="w-6 h-6" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STEP 5: AFFILIATE, POINTS & CLEAR BLOCKS */}
      {activeStep === 5 && (
        <div className="flex flex-col gap-6">
          <div className="bg-white rounded-md shadow-md p-6 flex flex-col gap-6">
            <h3 className="text-xl font-bold text-gray-800 border-b pb-3">
              การคำนวณรายได้ คะแนน และการล้างสลากคงเหลือ (งวด {roundDateToDate(configs?.roundDate)})
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* CLEAR BLOCKS ACTION */}
              <div className="flex flex-col justify-between p-6 bg-red-50 rounded-md border border-red-200 gap-4">
                <div>
                  <span className="text-lg font-bold text-red-800">1. ล้างและลบบล็อกสลากทั้งหมด (Clear Blocks)</span>
                  <p className="text-sm font-semibold text-red-600 mt-1">
                    ⚠️ จำเป็นต้องทำทุกครั้งหลังจบงวด: ดึงสลากที่เหลือออกจากบล็อกและลบทั้งหมดใน collection 'Blocks' ป้องกันสลากคงเหลืองวดนี้ติดค้างไปรวมกับงวดถัดไป
                  </p>
                </div>
                <button
                  onClick={handleClearAllBlocks}
                  disabled={loading}
                  className="py-3 px-4 text-sm font-bold rounded-md bg-red-600 text-white hover:bg-red-700 transition-all shadow-sm flex items-center justify-center gap-2"
                >
                  <FiTrash2 className="w-4 h-4" />
                  <span>ล้างสลากและลบ Blocks ทั้งหมด</span>
                </button>
              </div>

              {/* AFFILIATE DEPOSIT */}
              <div className="flex flex-col justify-between p-6 bg-gray-50 rounded-md border border-gray-200 gap-4">
                <div>
                  <span className="text-lg font-bold text-gray-800">2. คำนวณรายได้ Affiliate ประจำงวด</span>
                  <p className="text-sm font-semibold text-gray-500 mt-1">
                    คำนวณและบันทึกยอดรายได้ส่วนแบ่ง Affiliate ของผู้แนะนำประจำงวด
                  </p>
                </div>
                <button
                  onClick={handleAutoDepositAffiliate}
                  disabled={loading}
                  className="py-3 px-4 text-sm font-bold rounded-md bg-blue text-white hover:opacity-80 transition-all shadow-sm"
                >
                  💰 คำนวณรายได้ Affiliate
                </button>
              </div>

              {/* BUYER POINTS */}
              <div className="flex flex-col justify-between p-6 bg-gray-50 rounded-md border border-gray-200 gap-4">
                <div>
                  <span className="text-lg font-bold text-gray-800">3. คำนวณคะแนนสะสมผู้ซื้อ</span>
                  <p className="text-sm font-semibold text-gray-500 mt-1">
                    ประมวลผลเพิ่มคะแนนสะสมความดี/คะแนนซื้อสลากให้แก่ผู้ซื้อในงวดนี้
                  </p>
                </div>
                <button
                  onClick={handleAddRoundedPoint}
                  disabled={loading}
                  className="py-3 px-4 text-sm font-bold rounded-md bg-indigo-600 text-white hover:opacity-80 transition-all shadow-sm"
                >
                  ⭐ คำนวณคะแนนผู้ซื้อ
                </button>
              </div>

              {/* RESET EXPIRED POINTS */}
              <div className="flex flex-col justify-between p-6 bg-gray-50 rounded-md border border-gray-200 gap-4">
                <div>
                  <span className="text-lg font-bold text-gray-800">4. รีเซ็ตคะแนนหมดอายุ (สิ้นเดือน)</span>
                  <p className="text-sm font-semibold text-gray-500 mt-1">
                    ล้างคะแนนสะสมของสมาชิกที่ครบกำหนดหมดอายุในสิ้นเดือนนี้
                  </p>
                </div>
                <button
                  onClick={handleResetExpiredPoints}
                  disabled={loading}
                  className="py-3 px-4 text-sm font-bold rounded-md bg-amber-600 text-white hover:opacity-80 transition-all shadow-sm"
                >
                  ⌛ รีเซ็ตคะแนนหมดอายุ
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STEP NAVIGATION FOOTER */}
      <div className="flex justify-between items-center bg-white p-4 rounded-md shadow-md border-t border-gray-200">
        <button
          onClick={() => setActiveStep((prev) => Math.max(1, prev - 1))}
          disabled={activeStep === 1}
          className="py-2.5 px-6 text-sm font-bold rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 disabled:opacity-40 flex items-center gap-2"
        >
          <FiChevronLeft className="w-4 h-4" />
          <span>ขั้นตอนก่อนหน้า</span>
        </button>

        <span className="text-sm font-bold text-gray-600">
          ขั้นตอนที่ {activeStep} / 5
        </span>

        <button
          onClick={() => setActiveStep((prev) => Math.min(5, prev + 1))}
          disabled={activeStep === 5}
          className="py-2.5 px-6 text-sm font-bold rounded-md bg-blue text-white hover:opacity-80 disabled:opacity-40 flex items-center gap-2"
        >
          <span>ขั้นตอนถัดไป</span>
          <FiChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};
export default AllInOneDraw;
